import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaStrip } from "@/components/CtaStrip";

const DEFINITIONS = [
  { term: "Margin", def: "The difference in votes between the winning candidate and the runner-up in a constituency." },
  { term: "Vote Share", def: "A candidate's or party's votes as a percentage of total valid votes polled in a defined area." },
  { term: "Seat Change", def: "The net difference in seats won by a party between two comparable elections." },
  { term: "Vote-Share Change", def: "The difference in vote share between two elections, expressed in percentage points — never as percentage growth." },
  { term: "Majority", def: "More than half of the total seats in the house — 272 of 543 in the Lok Sabha." },
  { term: "Lead vs Result", def: "A lead is a provisional position during counting; a result is the formally declared outcome." },
];

const CHAPTERS = [
  {
    id: "approach",
    title: "Our Approach",
    body: [
      "Anlytx for Elections explicitly separates three layers: Data (reported election figures), Calculation (mechanically derived metrics like margin, seat change, vote-share change), and Interpretation (analytical conclusions drawn from numbers). We label each clearly.",
      "This separation is deliberate. Data can be verified. Calculations can be reproduced. Interpretations can be challenged. Keeping them distinct is what allows journalists and researchers to trust — and test — everything on the platform.",
    ],
  },
  {
    id: "sources",
    title: "Data Sources",
    body: [
      "Anlytx for Elections is built on official election commission sources — the Election Commission of India and State Election Commissions. Primary sources are documented per dataset; where secondary sources are used, they are identified as such.",
      "Source attribution travels with the data. Every figure on the platform can be traced back to the source it was ingested from.",
    ],
  },
  {
    id: "processing",
    title: "How Results Are Processed",
    body: [
      "During active counting, results are ingested continuously and timestamped at intake. Figures shown during counting are leads — provisional positions that can and do change as rounds complete. A constituency is marked as a declared result only when the official declaration is published.",
      "Provisional data is always labelled as provisional. Update frequency and the timestamp of the last update are displayed alongside live figures, so no number is ever presented without its currency.",
    ],
  },
  {
    id: "standardisation",
    title: "Standardisation",
    body: [
      "Candidate, party and constituency identities are standardised across elections. Name variants, transliteration differences and party name changes are resolved to stable internal identities, so a candidate or party can be tracked consistently across election cycles.",
      "Where a party has split, merged or changed name, the standardisation decision is recorded — not silently applied.",
    ],
  },
  {
    id: "calculations",
    title: "Calculations",
    body: [
      "Vote share is votes received divided by total valid votes polled, expressed as a percentage. Margin is the vote difference between winner and runner-up. Seat change is the net difference in seats won between two comparable elections. Vote-share change is expressed strictly in percentage points.",
      "We never describe a percentage-point movement as percentage growth: a move from 20% to 25% vote share is a 5 percentage-point gain, not '25% growth'. Every derived metric on the platform follows a published definition — a selection of which appears below.",
    ],
    definitions: true,
  },
  {
    id: "comparability",
    title: "Historical Comparability",
    body: [
      "Historical records are mapped across elections with delimitation and boundary changes taken into account. Constituency boundaries in India have been redrawn multiple times; where boundaries have changed materially, direct comparisons are flagged or withheld.",
      "When a comparison should not be made — because the constituency of 1977 is not the constituency of today — we say so, rather than present a misleading trend line.",
    ],
  },
  {
    id: "corrections",
    title: "Corrections & Revisions",
    body: [
      "When official sources revise published figures, we correct our records and keep the revision history. Corrections are applied to the affected data points and, where a figure was publicly displayed, the change is logged.",
      "We treat corrections as a normal part of working with official data — not as something to hide.",
    ],
  },
  {
    id: "limitations",
    title: "Known Limitations",
    body: [
      "Election data can tell you what happened, where, and by how much. It cannot tell you why. Vote-share patterns do not establish voter motivation, and constituency-level aggregates do not describe individual voters.",
      "Historical comparisons across delimited boundaries carry uncertainty even with careful mapping. Provisional counting figures are not final results. We disclose these limits because the credibility of the platform depends on it.",
    ],
  },
];

const Methodology = () => (
  <div data-testid="methodology-page">
    <Seo
      title="Data & Methodology — How Anlytx for Elections Handles Election Data | Anlytx for Elections"
      description="How Anlytx for Elections sources, standardises, calculates and corrects Indian election data — transparent definitions, provisional-data labelling and delimitation-aware historical comparisons."
    />

    <section className="relative overflow-hidden bg-[#0b1322]" data-testid="methodology-hero">
      <div className="absolute inset-0 grid-faint-dark opacity-70" />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
        <Reveal>
          <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">
            Data &amp; Methodology
          </p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
            Documented. Defined. Open to Scrutiny.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            Trust in election data is earned through transparency. This page documents how Anlytx for Elections sources,
            processes, calculates and corrects the data behind every figure on the platform.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white">
      <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
        {CHAPTERS.map((ch, i) => (
          <Reveal key={ch.id}>
            <article id={ch.id} className="grid gap-6 border-t border-slate-200 py-12 md:grid-cols-[110px_1fr]">
              <div className="font-mono-data text-5xl font-bold tracking-tight text-[#b91c1c]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{ch.title}</h2>
                <div className="mt-4 space-y-4">
                  {ch.body.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-slate-600">
                      {p}
                    </p>
                  ))}
                </div>
                {ch.definitions && (
                  <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="definitions-grid">
                    {DEFINITIONS.map((d) => (
                      <div
                        key={d.term}
                        className="rounded-lg border-l-[3px] border-[#b91c1c] bg-[#f8fafc] p-4"
                        data-testid={`definition-${d.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <p className="font-heading text-sm font-semibold text-slate-900">{d.term}</p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-600">{d.def}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <CtaStrip
      testid="methodology-cta-strip"
      heading="Questions About a Number You Saw?"
      subheading="Our research desk answers methodology and data queries from journalists, analysts and researchers."
      primary={{ label: "Ask a Data Question", to: "/contact?type=analyst" }}
      secondary={{ label: "Contact the Team", to: "/contact" }}
    />
  </div>
);

export default Methodology;
